import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Standard-Button: transparenter Hintergrund, bei Hover grün gefüllt
        default: "bg-transparent text-foreground border border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-green transition-all duration-200",
        // Primär-Aktion (CTA): wie default, gleicher Hover-Effekt
        primary: "bg-transparent text-foreground border border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-green transition-all duration-200",
        // Ausgewählt-Zustand: grüner Hintergrund, weiße Schrift
        selected: "bg-primary text-primary-foreground border-2 border-primary shadow-green transition-all duration-200",
        // Outline: wie default
        outline: "bg-transparent text-foreground border border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-green transition-all duration-200",
        // Secondary: dezenter, gleicher Hover
        secondary: "bg-transparent text-foreground border border-muted-foreground/30 hover:bg-primary hover:text-primary-foreground hover:border-primary hover:shadow-green transition-all duration-200",
        // Ghost: kein Rand, bei Hover grün
        ghost: "bg-transparent text-foreground hover:bg-primary hover:text-primary-foreground hover:shadow-green transition-all duration-200",
        // Hero für große CTAs: wie primary
        hero: "bg-transparent text-foreground border border-primary font-semibold hover:bg-primary hover:text-primary-foreground hover:shadow-green hover:scale-105 transition-all duration-300",
        // Destructive: rot
        destructive: "bg-transparent text-destructive border border-destructive hover:bg-destructive hover:text-destructive-foreground transition-all duration-200",
        // Link
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
